/**
 * The window to look for a visit in, read out of what the customer actually
 * said.
 *
 * "Tomorrow if you can" is an answer to "when?", and the form has a date range
 * that answers the same question — but the extractor cannot fill it, because
 * turning a phrase into two dates means picking dates nobody agreed to. So the
 * phrase goes into the notes and the range stays on its default, and the agent
 * on the phone re-reads the sentence they just heard and clicks the preset it
 * names.
 *
 * This closes that gap for the phrases that mean one obvious thing, and ONLY
 * those. It returns a preset the form already has rather than a pair of dates,
 * so the window is the same one the operator would have picked, and it returns
 * null the moment the sentence is doing anything more interesting than naming
 * a day — "the week of the 14th", "after my trip", "two weeks Tuesday". A
 * wrong guess here books a van on the wrong day; an absent guess costs a click.
 */

/** The date presets the visit finder offers, in the order they widen. */
export type PreferredWindow = 'today' | 'tomorrow' | 'week';

/**
 * Phrases that pin the window, most specific first.
 *
 * Order is the whole design: "the day after tomorrow" contains "tomorrow", and
 * "next week" contains "week", so the narrow readings have to be ruled out
 * before the broad ones are tried.
 */
const RULES: { window: PreferredWindow | null; test: RegExp }[] = [
  // Ruled out before anything else — each of these CONTAINS a phrase below it
  // but does not mean it.
  { window: null, test: /\bday after tomorrow\b/ },
  { window: null, test: /\bnext month\b|\bin (a|two|three|\d+) weeks?\b|\bweek of\b/ },
  { window: null, test: /\bnot? (before|until)\b/ },

  { window: 'today', test: /\b(asap|a\.s\.a\.p)\b|\bas soon as possible\b/ },
  { window: 'today', test: /\b(right away|straight away|immediately|urgent|emergency)\b/ },
  { window: 'today', test: /\btoday\b|\bthis (morning|afternoon|evening)\b|\btonight\b/ },

  { window: 'tomorrow', test: /\btomorrow\b|\btmrw\b|\bnext day\b/ },

  { window: 'week', test: /\b(this|next) week\b/ },
  { window: 'week', test: /\bnext (few|couple of) days\b|\bin a (few|couple of) days\b/ },
  /*
   * A weekday, bounded at BOTH ends.
   *
   * Alternation binds loosely, so `\bmon|tues|...` put the word boundary on
   * the first branch only and left every other branch matching any substring
   * anywhere. On an HVAC call that is not a rare accident: "the monitor is
   * broken", "the sunny side of the house" and "after sunset works" each
   * named no day and each auto-ran an availability search for the week.
   */
  {
    window: 'week',
    test: /\b(?:mon(?:day)?|tue(?:s|sday)?|wed(?:s|nes(?:day)?)?|thu(?:r|rs|rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)\b/,
  },
];

/**
 * Which preset the customer's own words point at, or null to leave the finder
 * where the operator left it.
 *
 * Deliberately not a date: the finder owns what "tomorrow" resolves to, and
 * two places resolving it means two answers on either side of midnight.
 */
export function preferredWindowFrom(spoken: string | null | undefined): PreferredWindow | null {
  const text = (spoken ?? '').toLowerCase();
  if (!text.trim()) return null;
  for (const rule of RULES) {
    if (rule.test.test(text)) return rule.window;
  }
  return null;
}

/**
 * The caller saying the day is open, which is not the same as saying nothing.
 *
 * `preferredWindowFrom` answers null for two very different sentences: one
 * that names no day the finder can act on ("the day after tomorrow", "week of
 * the 14th", a phrase nobody has seen before, a poll where the field did not
 * come back), and one that names the ABSENCE of a constraint. Only the second
 * is the caller telling you something new.
 *
 * It matters because Iris narrows off the call. Once she has put the finder on
 * tomorrow because the caller said tomorrow, "whenever suits you" is them
 * taking that back — and reading it as silence leaves the agent offering one
 * morning to someone who just opened up their whole week.
 *
 * Deliberately short and literal. Every phrase here has to mean an open day
 * and nothing else: "as soon as possible" is not open, it is today, and it is
 * read as today long before this is consulted.
 */
export function isOpenEndedTime(spoken: string | null | undefined): boolean {
  const text = (spoken ?? '').toLowerCase();
  if (!text.trim()) return false;
  return (
    /\bwhenever\b/.test(text) ||
    /\bany ?time\b/.test(text) ||
    /\bany day\b/.test(text) ||
    /\b(i am|i'm|we are|we're) flexible\b/.test(text) ||
    /\bno rush\b|\bnot in a rush\b|\bno hurry\b/.test(text) ||
    /\b(does not|doesn't|dont|don't) matter\b/.test(text) ||
    /\bup to you\b|\byou (pick|choose)\b/.test(text)
  );
}

/**
 * Morning, afternoon or evening, when the caller said one.
 *
 * Separate from the window because they answer different questions and fail
 * differently: getting the DAY wrong shows the agent a list they cannot offer,
 * while getting the half-day wrong only narrows a list they can widen again.
 * A sentence can name one, both or neither.
 */
export function preferredTimeOfDayFrom(
  spoken: string | null | undefined,
): 'morning' | 'afternoon' | 'evening' | null {
  const text = (spoken ?? '').toLowerCase();
  if (!text.trim()) return null;
  // "not in the morning" names the half-day it rules OUT.
  if (/\bnot?\s+(in\s+the\s+)?(morning|afternoon|evening)\b/.test(text)) return null;
  if (/\bmorning\b/.test(text)) return 'morning';
  if (/\bafternoon\b/.test(text)) return 'afternoon';
  if (/\b(evening|tonight|after work)\b/.test(text)) return 'evening';
  return null;
}
